import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

interface ForTutorsLandingProps {
  isAuthenticated: boolean;
  onStartPilotLogin: () => void;
}

interface FeatureItem {
  title: string;
  description: string;
}

const ForTutorsLanding: React.FC<ForTutorsLandingProps> = ({
  isAuthenticated,
  onStartPilotLogin,
}) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = React.useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    const previousDescription = document
      .querySelector('meta[name="description"]')
      ?.getAttribute('content');

    document.title = 'For Tutors | Exam Masters';

    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement('meta');
      descriptionMeta.setAttribute('name', 'description');
      document.head.appendChild(descriptionMeta);
    }

    descriptionMeta.setAttribute(
      'content',
      'Run your tutoring business in one place with Exam Masters: classes, sessions, homework, assessments, attendance, and student progress.',
    );

    return () => {
      document.title = previousTitle || 'Exam Masters';
      if (descriptionMeta) {
        descriptionMeta.setAttribute('content', previousDescription || '');
      }
    };
  }, []);

  const stepItems = useMemo(() => ([
    'Create your tutoring organization',
    'Create courses and classes',
    'Add or invite students',
    'Schedule tutoring sessions',
    'Run classes and track attendance',
    'Assign homework and assessments',
    'Review student progress',
  ]), []);

  const features = useMemo<FeatureItem[]>(() => ([
    {
      title: 'Student Management',
      description: 'Organize learners by organization and classroom with role-based membership workflows.',
    },
    {
      title: 'Courses and Classes',
      description: 'Create courses, categories, and classrooms for structured tutoring delivery.',
    },
    {
      title: 'Session Scheduling',
      description: 'Plan one-to-one or classroom sessions with teacher, student, date, and status tracking.',
    },
    {
      title: 'Online Class Links',
      description: 'Attach meeting links to sessions so tutors and students can join quickly from the hub.',
    },
    {
      title: 'Attendance Tracking',
      description: 'Capture join status and participation timeline for each scheduled session.',
    },
    {
      title: 'Homework and Assignments',
      description: 'Publish assignments, set due dates, and review submissions in one workflow.',
    },
    {
      title: 'Assessments',
      description: 'Create and run assessments with question banks, attempts, and result review.',
    },
    {
      title: 'Student Progress',
      description: 'Use dashboard and attempt insights to monitor completion and performance trends.',
    },
    {
      title: 'Teacher and Student Discussion',
      description: 'Use session discussion spaces to coordinate before and during classes.',
    },
    {
      title: 'AI-assisted Question Tools',
      description: 'Use built-in AI-supported question workflows for faster academic content operations.',
    },
  ]), []);

  const handleStartPilot = () => {
    if (isAuthenticated) {
      navigate('/analytics?pre=1');
      return;
    }

    onStartPilotLogin();
  };

  const handleSeeHowItWorks = () => {
    const section = document.getElementById('how-it-works');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const screenshotSlots = [
    'Admin dashboard',
    'Course and class management',
    'Session scheduling',
    'Homework and assignments',
    'Assessment workflows',
    'Student portal',
  ];

  return (
    <div style={{ width: '100%', overflowX: 'hidden', background: '#f8fafc' }}>
      <section
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          padding: isMobile ? '54px 16px 68px' : '72px 20px 86px',
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <h1 style={{ margin: 0, fontSize: isMobile ? '2rem' : '3rem', lineHeight: 1.12, fontWeight: 800 }}>
            Run Your Tutoring Business in One Place
          </h1>
          <p style={{ margin: '18px auto 0', maxWidth: 760, fontSize: isMobile ? '1rem' : '1.2rem', lineHeight: 1.55, opacity: 0.95 }}>
            Manage students, classes, online sessions, homework, assessments and attendance from one simple platform.
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginTop: 30 }}>
            <button
              type="button"
              onClick={handleStartPilot}
              style={{
                background: '#fff',
                color: '#4f46e5',
                border: 'none',
                borderRadius: 10,
                padding: '12px 24px',
                fontWeight: 700,
                fontSize: '0.98rem',
                cursor: 'pointer',
                minWidth: 180,
              }}
            >
              Start Free Pilot
            </button>
            <button
              type="button"
              onClick={handleSeeHowItWorks}
              style={{
                background: 'transparent',
                color: '#fff',
                border: '2px solid rgba(255,255,255,0.82)',
                borderRadius: 10,
                padding: '10px 22px',
                fontWeight: 700,
                fontSize: '0.96rem',
                cursor: 'pointer',
                minWidth: 180,
              }}
            >
              See How It Works
            </button>
          </div>
        </div>
      </section>

      <section id="how-it-works" style={{ padding: isMobile ? '40px 16px' : '58px 20px', background: '#fff' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? '1.6rem' : '2.1rem', color: '#111827', textAlign: 'center' }}>How It Works</h2>
          <p style={{ margin: '10px auto 26px', color: '#4b5563', maxWidth: 760, textAlign: 'center', lineHeight: 1.55 }}>
            Start fast with a practical rollout path built for real tutoring operations.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
            {stepItems.map((step, index) => (
              <div
                key={step}
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: '14px 14px',
                  background: '#f9fafb',
                }}
              >
                <div style={{
                  width: 30,
                  height: 30,
                  borderRadius: 999,
                  background: '#e0e7ff',
                  color: '#4338ca',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: 14,
                  flexShrink: 0,
                }}>
                  {index + 1}
                </div>
                <div style={{ color: '#1f2937', lineHeight: 1.45, fontWeight: 600 }}>{step}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: isMobile ? '40px 16px' : '58px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? '1.6rem' : '2.1rem', color: '#111827', textAlign: 'center' }}>Features for Tutoring Teams</h2>
          <p style={{ margin: '10px auto 26px', color: '#4b5563', maxWidth: 760, textAlign: 'center', lineHeight: 1.55 }}>
            Built to support the workflows tutors use every day.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
            {features.map((feature) => (
              <article key={feature.title} style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', padding: 16, textAlign: 'left' }}>
                <h3 style={{ margin: '0 0 8px', color: '#111827', fontSize: '1.02rem' }}>{feature.title}</h3>
                <p style={{ margin: 0, color: '#4b5563', lineHeight: 1.5, fontSize: '0.95rem' }}>{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: isMobile ? '40px 16px' : '58px 20px', background: '#fff' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? '1.6rem' : '2.1rem', color: '#111827', textAlign: 'center' }}>Product Screenshots and Demos</h2>
          <p style={{ margin: '10px auto 26px', color: '#4b5563', maxWidth: 760, textAlign: 'center', lineHeight: 1.55 }}>
            Replace these placeholders with your real screenshots for ad visitors.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 14 }}>
            {screenshotSlots.map((title) => (
              <div key={title} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, background: '#f9fafb' }}>
                <div style={{ border: '2px dashed #cbd5e1', borderRadius: 10, height: 154, background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontWeight: 700, fontSize: '0.9rem', textAlign: 'center', padding: 12 }}>
                  Screenshot placeholder
                </div>
                <div style={{ marginTop: 10, color: '#1f2937', fontWeight: 700, fontSize: '0.94rem' }}>{title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: isMobile ? '40px 16px' : '58px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? '1.6rem' : '2.1rem', color: '#111827', textAlign: 'center' }}>Who It Is For</h2>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 14, marginTop: 24 }}>
            {[
              'Individual tutors',
              'Small tutoring businesses',
              'Growing tutoring centres',
              'Organizations managing multiple teachers and students',
            ].map((group) => (
              <div key={group} style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', padding: 16, color: '#1f2937', fontWeight: 600 }}>
                {group}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: isMobile ? '44px 16px 60px' : '64px 20px 78px', background: '#111827', color: '#fff' }}>
        <div style={{ maxWidth: 920, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? '1.55rem' : '2.2rem', lineHeight: 1.2 }}>Ready to simplify your tutoring business?</h2>
          <p style={{ margin: '12px auto 24px', color: '#d1d5db', lineHeight: 1.55, maxWidth: 740 }}>
            We are currently accepting tutors and tutoring teams for early-access free pilot onboarding.
          </p>
          <button
            type="button"
            onClick={handleStartPilot}
            style={{
              background: '#4f46e5',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '12px 26px',
              fontWeight: 700,
              fontSize: '1rem',
              cursor: 'pointer',
              minWidth: 200,
            }}
          >
            Start Free Pilot
          </button>
        </div>
      </section>
    </div>
  );
};

export default ForTutorsLanding;
